import { ConfirmModal } from '@components/ConfirmModal';
import { EmptyState } from '@components/EmptyState';
import { PermissionGate } from '@components/PermissionGate';
import { useBankProfile } from '@contexts/BankProfileContext';
import {
	BookIcon,
	Button,
	CardItem,
	ChevronLeftIcon,
	Chip,
	FileIcons,
	ForwardIcon,
	List,
	Loader,
	Typography,
} from '@gurupay/gp-ui-library';
import { useDeleteTemplate } from '@hooks/useDeleteTemplate';
import { useTemplates } from '@hooks/useTemplates';
import { formatNumberAmount } from '@utils/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PaginationQuery } from 'types/response';
import { Capability } from 'types/roles';
import type { TemplateItemResponse } from 'types/templates';
import { TemplatePaymentType } from 'types/templates';

const paymentTypeChipLabel = {
	[TemplatePaymentType.INTERNAL]: 'eurPayments',
	[TemplatePaymentType.DOMESTIC]: 'eurPayments',
	[TemplatePaymentType.INTERNATIONAL]: 'internationalPayments',
};

const paymentUrlMapper = {
	[TemplatePaymentType.DOMESTIC]: 'sepa',
	[TemplatePaymentType.INTERNAL]: 'sepa',
	[TemplatePaymentType.INTERNATIONAL]: 'international',
};

interface TemplatesListProps {
	title?: string;
	params?: PaginationQuery;
	showButton?: boolean;
}

export const TemplatesList: FC<TemplatesListProps> = ({
	title,
	params = {},
	showButton,
}) => {
	const { t } = useTranslation('', { keyPrefix: 'templatesList' });
	const { hasCapability } = useBankProfile();
	const { data, isLoading, isError } = useTemplates(params);
	const templates: TemplateItemResponse[] = data?.items ?? [];
	const { mutateAsync: deleteTemplate } = useDeleteTemplate();

	return (
		<div className="space-y-4">
			{title && (
				<Typography className="flex flex-row gap-space-12" level="h3">
					{title}
					{!isError && !!templates?.length && !isLoading && (
						<Link to="/templates">
							<ChevronLeftIcon className="rotate-180 text-brand-secondary-700" />
						</Link>
					)}
				</Typography>
			)}
			{isLoading ? (
				<div className="text-center my-space-40">
					<Loader />
				</div>
			) : templates.length ? (
				<List>
					{templates.map((item) => (
						<CardItem
							key={item.uuid}
							actions={
								<div className="flex gap-space-16">
									<PermissionGate capability={Capability.MANAGE_TEMPLATE}>
										<ConfirmModal
											cancelKey={t('confirmRemove.cancel')}
											id={item.uuid}
											onRemove={deleteTemplate}
											submitKey={t('confirmRemove.submit')}
											titleKey={t('confirmRemove.title', { templateName: item.name })}
										/>
									</PermissionGate>
									<PermissionGate capability={Capability.CREATE_PAYMENT}>
										<Button
											href={`/payments/${paymentUrlMapper[item.payment_type]}?templateId=${item.uuid}`}
											size="sm"
											variant="secondary"
										>
											<ForwardIcon className="w-spape-16 h-space-16" />
											{t('pay')}
										</Button>
									</PermissionGate>
								</div>
							}
							className="max-mobile:flex-col not-last:border-b  border-neutral-100"
							icon={<FileIcons className="text-white w-space-24 h-space-24" />}
							iconClassName="bg-neutral-800"
							linkClassName="not-last:border-b border-neutral-100 block"
							subTitle={
								item?.amount ? (
									<Typography className="text-neutral-600 text-sm-medium" level="text">
										{`${formatNumberAmount((item.amount / 100).toFixed(2))} ${item?.currency}`}
									</Typography>
								) : null
							}
							title={item.name}
							to={
								hasCapability(Capability.MANAGE_TEMPLATE)
									? `/templates/${item.uuid}`
									: undefined
							}
						>
							<div className="overflow-hidden py-space-2">
								{!!item?.beneficiary?.name && (
									<Typography className="text-neutral-600 text-sm-medium" level="text">
										{item?.beneficiary?.name}
									</Typography>
								)}
								<span className="text-neutral-600 text-sm-medium mr-space-8">
									{item?.beneficiary?.account}
								</span>
								<Chip
									className="text-neutral-950 mx-space-2"
									color="primary"
									label={t(paymentTypeChipLabel[item.payment_type])}
									size="small"
									variant="outlined"
								/>
							</div>
						</CardItem>
					))}
				</List>
			) : (
				<EmptyState
					buttonProps={
						showButton
							? {
									children: t('empty.buttonText'),
									href: '/templates/new',
								}
							: undefined
					}
					icon={<BookIcon />}
					subTitle={t('empty.subtitle')}
					title={t('empty.title')}
				/>
			)}
		</div>
	);
};
